import React, { useRef, useEffect, useLayoutEffect, useState, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import { useHistory } from "react-router-dom";
import { useRecoilState, useRecoilValue } from "recoil";
import moment from "moment";
import { useMutation } from "@apollo/react-hooks";
import { useQuery, useSubscription } from "@apollo/react-hooks";
import { Box, Card, Link, Typography, Tab, Tabs } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  MyHrContractCard,
  MyHrStickyPersonalDataHeader,
} from "my-hr/components";
import { useMyHrContract, useMyHrEmployee, useMyHrUserRolesDataByContract } from "my-hr/hooks";
import {
  MyLeaveAllLeavesWithCoverage,
  MyLeaveCardHeader,
  MyLeaveCardMessageBox,
  MyLeaveInfoDialog,
  MyLeaveLeaveCards,
} from "my-leave/components";
import {
  MY_LEAVE_APPROVE_REQUEST,
  MY_LEAVE_GET_REQUESTS_BY_CONTRACT,
  MY_LEAVE_REJECT_REQUEST,
  MY_LEAVE_REQUESTS_SUBSCRIPTION,
} from "my-leave/graphql";
import {
  useMyLeaveEvents,
  useMyLeaveExaminersName,
  useMyLeaveIsExaminerContract,
  useMyLeaveRequest,
  useMyLeaveRoleTabs,
} from "my-leave/hooks";
import {
  OcCard,
  OcCardButtonBar,
  OcPageNameTitle,
  OcPageTransition,
} from "oc/components";
import {
  useOcClientState,
  useOcCurrentUserState,
  useOcIsMobile,
  useOcPolling,
  useOcSnackbar,
} from "oc/hooks";
import {
  myLeaveCurrentDateState,
  ocRoleFiltersState,
  ocScrollTriggerState,
} from "states";

export const MyLeaveUserRequestPage = () => {
  const { t, i18n } = useTranslation();
  const history = useHistory();
  const isMobile = useOcIsMobile();
  const theme = useTheme();

  let { product, role, contract, id } = useParams<{
    product: string;
    role: string;
    contract: string;
    id: string;
  }>();

  let { contractCode } = useOcCurrentUserState();

  const { data: examinersData } = useMyLeaveExaminersName({
    locale: i18n.language,
  });

  let now = new Date();

  const { data, error, refetch } = useQuery(MY_LEAVE_GET_REQUESTS_BY_CONTRACT, {
    fetchPolicy: "cache-and-network",
    pollInterval: useOcPolling(1000 * 60),
    skip: !contract,
    variables: { contract: contract, year: now.getFullYear().toString() },
  });

  if (error) {
    console.log({
      component: "MyLeaveRequests",
      errorDescription:
        "Error occured when fetch data with MY_LEAVE_GET_REQUESTS_BY_CONTRACT query",
      error: error,
    });
  }

  const { error: subscriptionError } = useSubscription(
    MY_LEAVE_REQUESTS_SUBSCRIPTION,
    {
      onData: () => {
        refetch();
      },
    },
  );

  if (subscriptionError) {
    console.log({
      component: "MyLeaveRequests",
      errorDescription:
        "Error occured when subscribe data with MY_LEAVE_REQUESTS_SUBSCRIPTION query",
      error: error,
    });
  }

  type LeaveRequestItem = {
    type: string;
    state: string;
    [key: string]: any;
  };

  let resultData = data?.leaveRequestsByContract || [];
  const requests = resultData.filter(
    (item: LeaveRequestItem) =>
      item.type === "leave" && item.state === "requested",
  );

  const [showInfoDialog, setShowInfoDialog] = useOcClientState<boolean>(
    "showInfoDialog",
    false,
  );

  const [currentDate, setCurrentDate] = useRecoilState(myLeaveCurrentDateState);

  const [loading, setLoading] = useState(false);

  const { data: contractData } = useMyHrContract({
    contract,
  });

  let employeeId = contractData?.employee?.code;
  const { data: employeeData } = useMyHrEmployee({
    employeeId,
  });

  const { leaveRequest, leaveRequestLoading } = useMyLeaveRequest({
    _id: id,
  });

  /** jump to leave request start date on coverage calendar */
  useEffect(() => {
    if (currentDate && leaveRequest) {
      let startDateString = moment(leaveRequest?.startDate).format(
        "YYYY-MM-DD",
      );
      let currentDateString = moment(currentDate).format("YYYY-MM-DD");
      if (currentDateString !== startDateString) {
        setCurrentDate(new Date(startDateString));
      }
    }
    // eslint-disable-next-line
  }, []);

  const pageRef = useRef<HTMLDivElement>(null);

  const [scrollTrigger, setScrollTrigger] = useRecoilState(ocScrollTriggerState);
  const [pageWidth, setPageWidth] = useOcClientState<number>("pageWidth", 0);

  const handleScroll = useCallback(() => {
    const triggerY = 100;
    const scrollTop = document.scrollingElement?.scrollTop ?? 0;
    setScrollTrigger(scrollTop >= triggerY ? "show" : "hide");
  }, [setScrollTrigger]);

  const handleResize = useCallback(() => {
    const width = pageRef.current?.clientWidth;
    if (width && width !== pageWidth) {
      setPageWidth(width);
    }
  }, [pageWidth, setPageWidth]);

  useLayoutEffect(() => {
    handleResize();
    handleScroll();

    window.addEventListener("scroll", handleScroll);
    window.addEventListener("resize", handleResize);

    return () => {
      window.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", handleResize);
    };
  }, [handleScroll, handleResize]);

  const [message, setMessage] = useOcClientState("newMessageText", "");

  const { setOpenMessage } = useOcSnackbar();

  const { hasRole, data: rolesData } = useMyHrUserRolesDataByContract({ contract: contractCode });

  // Check if user has the required role from URL
  useEffect(() => {
    if (role && contractCode) {
      const requiredRole = `MyLeave.${role}`;
      if (!hasRole([requiredRole])) {
        const translatedRole = t(`MY_LEAVE_ROLE_${role.toUpperCase()}`);
        setOpenMessage({
          type: "error",
          message: t("MY_LEAVE_NO_PERMISSION_FOR_ROLE_MESSAGE", { role: translatedRole }),
        });
        history.push("/my-leave/dashboard");
      }
    }
  }, [role, contractCode, hasRole, history, setOpenMessage, t]);

  // Available roles with tabs
  const { roles, selectedTab } = useMyLeaveRoleTabs({ rolesData, currentRole: role });

  const handleTabChange = (_: React.SyntheticEvent, newValue: number) => {
    const newRole = roles[newValue]?.key;
    if (newRole && newRole !== role) {
      history.replace(`/my-leave/user-request/${product}/${newRole}/${contract}/${id}`);
    }
  };

  const [approveLeaveRequest] = useMutation(MY_LEAVE_APPROVE_REQUEST, {
    onCompleted: () => {
      let contract = leaveRequest?.contract;
      setLoading(false);
      setMessage("");
      setOpenMessage({
        type: "success",
        message: t("MY_LEAVE_APPROVE_LEAVE_REQUEST_SUCCEEDED"),
      });
      let uri = `/my-leave/user-requests/${product}/${role}/${contract}`;
      if (requests.length <= 1) {
        uri = "/my-leave/approve-requests";
      }
      history.push(uri);
    },
    onError: (error) => {
      setLoading(false);
      const errorType = (error?.graphQLErrors?.[0]?.extensions?.exception as any)?.type;

      let message: string;
      if (errorType && errorType.startsWith("MY_LEAVE_")) {
        message = t(errorType);
      } else {
        message = t("MY_LEAVE_APPROVE_LEAVE_REQUEST_FAILED");
      }

      setOpenMessage({
        type: "error",
        message,
      });
    },
  });

  const [rejectLeaveRequest] = useMutation(MY_LEAVE_REJECT_REQUEST, {
    onCompleted: () => {
      setLoading(false);
      setMessage("");
      setOpenMessage({
        type: "success",
        message: t("MY_LEAVE_REJECT_LEAVE_REQUEST_SUCCEEDED"),
      });
      let uri = `/my-leave/user-requests/${product}/${role}/${contract}`;
      if (requests.length <= 1 && role === "examiner") {
        uri = "/my-leave/approve-requests";
      }
      history.push(uri);
    },
    onError: (error) => {
      setLoading(false);
      const errorType = (error?.graphQLErrors?.[0]?.extensions?.exception as any)?.type;

      let message: string;
      if (errorType && errorType.startsWith("MY_LEAVE_")) {
        message = t(errorType);
      } else {
        message = t("MY_LEAVE_REJECT_LEAVE_REQUEST_FAILED");
      }

      setOpenMessage({
        type: "error",
        message,
      });
    },
  });

  const handleCancel = () => {
    let contract = leaveRequest?.contract;
    let uri = `/my-leave/user-requests/${product}/${role}/${contract}`;
    history.push(uri);
  };

  let locale = i18n.language;

  const handleReject = () => {
    if (!message) {
      setShowInfoDialog(true);
      return;
    }
    let _id = leaveRequest?._id;
    if (_id) {
      setLoading(true);
      rejectLeaveRequest({ variables: { _id, message, locale, role } });
    }
  };

  const handleApprove = () => {
    let _id = leaveRequest?._id;
    if (_id) {
      setLoading(true);
      approveLeaveRequest({ variables: { _id, message, locale, role } });
    }
  };

  let { data: isExaminerContract } = useMyLeaveIsExaminerContract({
    contract: leaveRequest?.contract,
  });

  let isExaminer = isExaminerContract || false;

  let disabled = false;

  const endDate = moment().subtract(1, "month").startOf("month").toDate();
  if (
    new Date(leaveRequest?.endDate) < endDate &&
    leaveRequest?.state === "requested"
  ) {
    disabled = true;
  }

  if (leaveRequest?._id?.startsWith("jd-")) {
    disabled = true;
  }


  if (
    (role !== "assigner" &&
      leaveRequest?.state === "assigned")) {
    disabled = true;
  }

  if (loading) {
    disabled = true;
  }

  let currentMonth = new Date().getMonth();
  let startOfPeriod = moment().startOf("year");

  if (currentMonth === 0) {
    startOfPeriod = moment().startOf("year").subtract(1, "month");
  }

  if (moment(leaveRequest?.startDate).isBefore(startOfPeriod)) {
    disabled = true;
  }

  // Separate disabled states for approve and reject buttons
  // Approve button: Only examiner can approve
  // Reject button:
  //   - For "assigned" state: assigner can reject
  //   - For "requested" or "approved" state: only examiner can reject

  let approveDisabled = disabled;
  let rejectDisabled = disabled;

  // Approve button requires examiner permission
  if (!isExaminer) {
    approveDisabled = true;
  }

  // Reject button logic based on leave request state
  if (leaveRequest?.state === "assigned") {
    // For assigned state: examiner OR assigner can reject
    if (!isExaminer && role !== "assigner") {
      rejectDisabled = true;
    }
  } else {
    // For requested/approved state: only examiner can reject
    if (!isExaminer) {
      rejectDisabled = true;
    }
  }

  if (leaveRequest?.state === "rejected") {
    approveDisabled = true;
    rejectDisabled = true;
  }

  if (contractCode === leaveRequest?.contract && examinersData?.length !== 0) {
    if (leaveRequest?.state !== "assigned") {
      approveDisabled = true;
      rejectDisabled = true;
    }
  }

  const handleCurrentDateChange = (date: Date | string) => {
    setCurrentDate(typeof date === "string" ? new Date(date) : date);
  };

  const roleFilters = useRecoilValue(
    ocRoleFiltersState(contract + product + role),
  );

  const handleCloseInfoDialog = () => {
    setShowInfoDialog(false);
  };

  let currentYear = leaveRequest
    ? new Date(leaveRequest?.startDate).getFullYear()
    : new Date().getFullYear();

  const skippableLeaves = ["recorded", "cancelled"];
  const showNewMessageBox = !skippableLeaves.includes(leaveRequest?.state);

  let viewedByAdmin = role === "viewer" ? true : false;

  let leaveCardTitle = "";

  switch (leaveRequest?.leaveType) {
    case "S":
      leaveCardTitle = "MY_LEAVE_NEW_LEAVE_BASE";
      break;
    case "T":
      leaveCardTitle = "MY_LEAVE_LEAVE_STUDY";
      break;
    case "D":
      leaveCardTitle = "MY_LEAVE_LEAVE_ESZJTV";
      break;
    case "Q":
      leaveCardTitle = "MY_LEAVE_COVID_PLUS_10";
      break;
    default:
    // TODO implement default leave error message
  }

  const {
    loadEvents,
    data: leavesData,
    loading: leaveEventsLoading,
    eventsByDays,
  } = useMyLeaveEvents({
    roleFilters: roleFilters,
    currentDate: moment(currentDate).toDate(),
    viewedByAdmin: viewedByAdmin,
    contract: contract,
    role: role,
  });

  useEffect(() => {
    loadEvents();
    if (!currentDate) {
      setCurrentDate(new Date());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentDate, setCurrentDate]);

  if (!leaveRequest && !leaveRequestLoading) {
    return (
      <Box>
        <MyHrStickyPersonalDataHeader
          pageWidth={pageWidth}
          employee={employeeData}
          contract={contractData}
          trigger={scrollTrigger === "show" ? true : false}
        />
        <MyHrContractCard employee={employeeData} contract={contractData} />
        <Box height="52px" />
        <Box
          display="flex"
          justifyContent="center"
          flexDirection="column"
          alignItems="center"
        >
          <Typography variant="h4">
            {t("MY_LEAVE_REQUEST_HAS_BEEN_WITHDRAWN")}
          </Typography>
          <Box height="32px" />
          <Link
            href="#/my-leave/approve-requests"
            rel="noreferrer"
            style={{ textDecoration: "none", cursor: "pointer" }}
          >
            <Typography variant="body1" style={{ color: "#CC0033" }}>
              {t("MY_TAX_TO_APPLICATIONS")}
            </Typography>
          </Link>
        </Box>
      </Box>
    );
  }

  return (
    <OcPageTransition>
      <Box ref={pageRef}>
        <MyHrStickyPersonalDataHeader
          pageWidth={pageWidth}
          employee={employeeData}
          contract={contractData}
          trigger={scrollTrigger === "show" ? true : false}
        />
        <OcPageNameTitle
          title={t("MY_LEAVE_USER_REQUEST_PAGE_TITLE")}
          goBack={true}
        />
        {roles.length > 1 && (
          <Box padding={isMobile ? "0px 0px 16px 0px" : "0px 16px 16px 16px"}>
            <Box bgcolor="#E3E5E5">
              <Tabs
                value={selectedTab}
                indicatorColor="primary"
                textColor="primary"
                variant="scrollable"
                scrollButtons="auto"
                onChange={handleTabChange}
                aria-label="role tabs"
              >
                {roles.map((r) => (
                  <Tab key={r.key} id={r.key} label={r.label} />
                ))}
              </Tabs>
            </Box>
          </Box>
        )}
        <MyHrContractCard employee={employeeData} contract={contractData} />
        <Box padding={isMobile ? "16px 0px" : "16px"}>
          <Box display="flex" flexDirection={isMobile ? "column" : "row"}>
            <Box width={isMobile ? "100%" : "600px"}>
              <Card
                elevation={0}
                style={{
                  borderRadius: "8px",
                  padding: "36px 24px",
                }}
              >
                <Box
                  style={{
                    textAlign: "center",
                    fontSize: 34,
                    fontWeight: 400,
                    letterSpacing: 0.25,
                  }}
                >
                  {t("OC_REQUEST")}
                </Box>
                <Box height="24px" />
                <Box
                  style={{
                    height: "1px",
                    backgroundColor: theme.palette.gray30.main,
                  }}
                />
                <Box height="24px" />
                <MyLeaveCardHeader
                  employee={employeeData}
                  leaveRequest={leaveRequest}
                  loading={leaveRequestLoading && !leaveRequest}
                  isMobile={isMobile}
                />
                <Box height="24px" />
                {showNewMessageBox && (
                  <MyLeaveCardMessageBox
                    leaveRequest={leaveRequest}
                    loading={leaveRequestLoading && !leaveRequest}
                    disabled={disabled}
                    role={role}
                  />
                )}
                <OcCardButtonBar
                  leftButtonText={t("OC_REJECT")}
                  leftButtonColor={
                    leaveRequest?.state !== "rejected" ? "primary" : undefined
                  }
                  leftButtonDisabled={rejectDisabled}
                  handleLeftButton={handleReject}
                  leftButtonId="myleave-user-request-page-leave-reject"
                  rightButtonText={t("OC_CANCEL")}
                  handleRightButton={handleCancel}
                  rightButtonColor="secondary"
                  rightButtonId="myleave-user-request-page-leave-cancel"
                  handlePrimaryButton={handleApprove}
                  primaryButtonText={
                    !(
                      leaveRequest?.state === "approved" ||
                      leaveRequest?.state === "assigned"
                    )
                      ? t("OC_APPROVE")
                      : ""
                  }
                  primaryButtonDisabled={approveDisabled}
                  primaryButtonId="myleave-user-request-page-leave-approve"
                />
              </Card>
            </Box>
            <Box width="16px" />
            {isMobile && <Box height="16px" />}
            {leaveRequest?.type === "leave" && (
              <Box width={isMobile ? "100%" : "600px"}>
                <OcCard label={t(leaveCardTitle)}>
                  <MyLeaveLeaveCards
                    selectedYear={currentYear}
                    contractCode={contract}
                    showFilter={leaveRequest?.leaveType}
                  />
                </OcCard>
              </Box>
            )}
          </Box>
          <Box height="16px" />
          <MyLeaveAllLeavesWithCoverage
            cardTitle={t("MY_LEAVE_LEAVES_AND_REMOTE_WORKS")}
            currentDate={currentDate}
            onCurrentDateChange={handleCurrentDateChange}
            monthFiltering={false}
            viewedByAdmin={viewedByAdmin}
            leavesData={leavesData}
            loading={leaveEventsLoading}
            eventsByDays={eventsByDays}
          />
        </Box>
        <MyLeaveInfoDialog
          showDialog={showInfoDialog}
          closeDialog={handleCloseInfoDialog}
          title={
            leaveRequest?.type === "leave"
              ? t("MY_LEAVE_REJECT_REQUEST")
              : t("MY_LEAVE_REJECT_REMOTE_WORK_REQUEST")
          }
          text={t("MY_LEAVE_REJECT_LEAVE_REQUEST_TEXT")}
          buttonLabel={t("OC_ALL_RIGHT")}
        />
      </Box>
    </OcPageTransition>
  );
};

export default MyLeaveUserRequestPage;
